// State and operations of the Striping Plan map tab (spec 2026-10-14 Parts A-B): which
// route is being edited, add mode, the selected point, undo/redo, and the DOT import.
// The list itself lives on the route (useRsaPaths actions); this hook only reads it.
import { useCallback, useMemo, useRef, useState } from 'react';
import { listOf, MAX_STRIPING_POINTS } from '../InternalComponents/stripingList';
import {
  laneLinesOfPath, orderedRows, pathSegmentNumbers, stripingRoadsOfPath,
} from '../InternalComponents/stripingIntersections';
import {
  featureCount, featuresToCandidates, importNoteText, planDotImport, readDotZip,
} from '../InternalComponents/stripingDotImport';
import {
  createHistory, recordFor, redoHistory, undoHistory,
} from '../InternalComponents/stripingHistory';

const NO_ROADS = { roads: [], problems: [] };
const NO_LINES = [];
const NO_ROWS = [];

export function useStripingEditor({
  paths = [], lanes = [], actions, preferredPathId = null, active = true,
}) {
  const [selectedPathId, setSelectedPathIdState] = useState(null);
  const [addMode, setAddMode] = useState(false);
  const [selectedPointIdState, setSelectedPointId] = useState(null);
  const [focus, setFocus] = useState(null);
  const [importNote, setImportNote] = useState(null);
  const [history, setHistory] = useState(() => createHistory(null));

  // The selected route while it exists, else the user's active route, else the first one
  // (a deleted route falls back).
  const path = paths.find((p) => p.id === selectedPathId)
    || paths.find((p) => p.id === preferredPathId) || paths[0] || null;
  const pathId = path?.id ?? null;
  const list = listOf(path);

  // Nothing is computed while the tab is closed (the editor stays mounted with the map).
  const laneLines = useMemo(
    () => (active && path ? laneLinesOfPath(path, lanes) : NO_LINES),
    [active, path, lanes],
  );
  const rows = useMemo(
    () => (active && path ? orderedRows(path, lanes, laneLines) : NO_ROWS),
    [active, path, lanes, laneLines],
  );
  const roads = useMemo(
    () => (active && path ? stripingRoadsOfPath(path, lanes, pathSegmentNumbers(paths)[path.id]) : NO_ROADS),
    [active, path, lanes, paths],
  );

  // Operations read the latest render's route/list rather than their own closure's, so a
  // stale closure (a map listener, an import that finished after a re-render) acts on the
  // current route. The history is mirrored in a ref so two operations before a re-render
  // build on each other instead of overwriting.
  const latest = useRef(null);
  latest.current = { pathId, list, laneLines };
  const historyRef = useRef(history);

  const commitHistory = (h) => {
    historyRef.current = h;
    setHistory(h);
  };
  const historyOf = (id) => (historyRef.current.pathId === id ? historyRef.current : createHistory(id));
  const record = () => {
    const cur = latest.current;
    commitHistory(recordFor(historyRef.current, cur.pathId, cur.list));
  };

  const ownHistory = history.pathId === pathId ? history : createHistory(pathId);
  // Only a point of the current route's list counts as selected.
  const selectedPointId = rows.some((r) => r.id === selectedPointIdState) ? selectedPointIdState : null;

  const setSelectedPathId = useCallback((id) => {
    setSelectedPathIdState(id);
    setSelectedPointId(null);
    setAddMode(false);
    setImportNote(null);
  }, []);

  const addPoint = (lat, lng) => {
    const cur = latest.current;
    if (!cur.pathId) return null;
    if (cur.list.length >= MAX_STRIPING_POINTS) {
      setImportNote({ kind: 'error', text: `A route holds at most ${MAX_STRIPING_POINTS} intersections.` });
      return null;
    }
    record();
    const id = actions.addStripingIntersection(cur.pathId, { lat, lng });
    setSelectedPointId(id);
    return id;
  };

  const movePoint = (id, lat, lng) => {
    const cur = latest.current;
    if (!cur.pathId) return;
    record();
    actions.moveStripingIntersection(cur.pathId, id, lat, lng);
  };

  const renamePoint = (id, name) => {
    const cur = latest.current;
    if (!cur.pathId) return;
    record();
    actions.renameStripingIntersection(cur.pathId, id, name);
  };

  const deletePoint = (id) => {
    const cur = latest.current;
    if (!cur.pathId) return;
    record();
    actions.deleteStripingIntersection(cur.pathId, id);
    setSelectedPointId((sel) => (sel === id ? null : sel));
  };

  const clearAll = () => {
    const cur = latest.current;
    if (!cur.pathId || !cur.list.length) return;
    record();
    actions.setStripingIntersections(cur.pathId, []);
    setSelectedPointId(null);
  };

  const importPoints = (candidates, unusable = 0) => {
    const cur = latest.current;
    const plan = planDotImport({ candidates, lines: cur.laneLines, existing: cur.list, unusable });
    if (cur.pathId && plan.added.length) {
      record();
      actions.addStripingIntersections(cur.pathId, plan.added);
    }
    setImportNote({ kind: 'info', text: importNoteText(plan) });
    return plan;
  };

  const importFile = async (file) => {
    const startPathId = latest.current.pathId;
    try {
      const layers = await readDotZip(file);
      if (latest.current.pathId !== startPathId) {
        setImportNote({ kind: 'error', text: 'Import cancelled: the route changed while the file was being read.' });
        return null;
      }
      const candidates = featuresToCandidates(layers);
      return importPoints(candidates, featureCount(layers) - candidates.length);
    } catch (e) {
      setImportNote({ kind: 'error', text: `Import failed: ${e?.message || e}` });
      return null;
    }
  };

  const undo = () => {
    const cur = latest.current;
    const r = undoHistory(historyOf(cur.pathId), cur.list);
    if (!r) return;
    commitHistory(r.history);
    actions.setStripingIntersections(cur.pathId, r.list);
  };

  const redo = () => {
    const cur = latest.current;
    const r = redoHistory(historyOf(cur.pathId), cur.list);
    if (!r) return;
    commitHistory(r.history);
    actions.setStripingIntersections(cur.pathId, r.list);
  };

  const focusPoint = (row) => {
    setSelectedPointId(row.id);
    setFocus({ lat: row.lat, lng: row.lng, nonce: Date.now() });
  };

  return {
    pathId, path, setSelectedPathId, addMode, setAddMode, selectedPointId,
    selectPoint: setSelectedPointId, focus, focusPoint, importNote, rows, laneLines, roads,
    canUndo: ownHistory.past.length > 0, canRedo: ownHistory.future.length > 0, undo, redo,
    addPoint, movePoint, renamePoint, deletePoint, clearAll, importPoints, importFile,
  };
}
